import { VoiceProduct } from '@shared/models/voice-product.model';
import { Selector } from '@ngxs/store';
import { CustomerProductsStateModel, CustomerProductState } from './customer-product.state';
import { BindingType } from '@shared/models/subscription-offering.model';
import { PortingWindowsResponse } from '../../modules/subscription/services/porting.service';
import { CustomerSelectors } from '../../modules/details/states/customer.selectors';
import { CustomerStateModel } from '../../modules/details/models/customer.model';
import { SubscriptionHelperService } from '@sales/services/subscription-helper.service';
import { InFlightOrder, InFlightOrderItemTypes } from '@app/core/models/in-flight-order.model';
import { OfferingState, OfferingStateModel } from '../offering/offering.state';
import { VoiceSubscriptionOffering } from '@sales/modules/subscription/models/offerings-response.model';

export class CustomerProductSelectors {
  @Selector([CustomerProductState])
  static getState(state: CustomerProductsStateModel): CustomerProductsStateModel {
    return state;
  }

  @Selector([CustomerProductState])
  static getAllVoiceProducts(state: CustomerProductsStateModel): VoiceProduct[] {
    return state.products;
  }

  @Selector([CustomerProductState])
  static getExistingVoiceProducts(state: CustomerProductsStateModel): VoiceProduct[] {
    return state.products.filter(p => p.isExistingSubscription);
  }

  @Selector([OfferingState, CustomerProductSelectors.getExistingVoiceProducts])
  static getOfferingsIncludingCustomerBTL(
    offeringState: OfferingStateModel,
    existingProducts: VoiceProduct[]
  ): VoiceSubscriptionOffering[] {
    const byId = new Map<string, VoiceSubscriptionOffering>();
    for (const offering of offeringState.products ?? []) {
      if (offering?.id) byId.set(offering.id, offering);
    }
    for (const product of existingProducts) {
      for (const info of product.allowedSubscriptionOfferings ?? []) {
        const offering = info?.subscriptionOffering;
        if (offering?.id && !byId.has(offering.id)) byId.set(offering.id, offering);
      }
    }
    return [...byId.values()];
  }

  @Selector([CustomerProductSelectors.getOfferingsIncludingCustomerBTL])
  static getManualRegistrationOfferingsIncludingCustomerBTL(
    offerings: VoiceSubscriptionOffering[]
  ): VoiceSubscriptionOffering[] {
    return offerings.filter(
      offering => offering.bindings?.length > 0 && offering.bindings.some(b => b.bindingType === BindingType.Handset)
    );
  }

  @Selector([CustomerProductSelectors.getExistingVoiceProducts])
  static getChangedVoiceSubscriptions(existingCustomerProducts: VoiceProduct[]) {
    return SubscriptionHelperService.getChangedVoiceSubscriptions(existingCustomerProducts);
  }

  @Selector([CustomerProductSelectors.getExistingVoiceProducts])
  static getUnchangedExistingVoiceSubscriptions(existingCustomerProducts: VoiceProduct[]) {
    return existingCustomerProducts.filter(existingProduct => {
      const changedProducts = this.getChangedVoiceSubscriptions(existingCustomerProducts);
      return !changedProducts.some(changedProduct => changedProduct.id === existingProduct.id);
    });
  }

  @Selector([CustomerProductSelectors.getNewVoiceProducts, CustomerProductSelectors.getChangedVoiceSubscriptions])
  static newAndChangedVoiceProducts(
    newVoiceSubscriptions: VoiceProduct[],
    changedVoiceSubscriptions: VoiceProduct[]
  ): VoiceProduct[] {
    return [...newVoiceSubscriptions, ...changedVoiceSubscriptions];
  }

  @Selector([CustomerSelectors.inFlightVoiceOrders, CustomerProductSelectors.getExistingVoiceProducts])
  static inFlightVoiceOrdersUnprocessed(
    inFlightOrders: InFlightOrder[],
    existingCustomerProducts: VoiceProduct[]
  ): InFlightOrder[] {
    if (!!existingCustomerProducts) {
      return inFlightOrders.filter(
        inFlightOrder =>
          !existingCustomerProducts.some(
            product =>
              product.subscription.msisdn === inFlightOrder.aNumber &&
              (inFlightOrder.orderItemType === InFlightOrderItemTypes.CreateSubscription ||
                inFlightOrder.orderItemType === InFlightOrderItemTypes.CreateSubscriptionV2)
          )
      );
    }

    return inFlightOrders;
  }

  @Selector([CustomerProductSelectors.getExistingVoiceProducts])
  static getMsisdnsOnExistingSubscriptions(existingVoiceProducts: VoiceProduct[]): string[] {
    return existingVoiceProducts && existingVoiceProducts.length > 0
      ? existingVoiceProducts.map((existingVoiceProduct: VoiceProduct) => existingVoiceProduct.subscription.msisdn)
      : [];
  }

  @Selector([CustomerProductState])
  static getNewVoiceProducts(state: CustomerProductsStateModel): VoiceProduct[] {
    return state.products.filter(p => !p.isExistingSubscription);
  }

  @Selector([CustomerProductSelectors.getExistingVoiceProducts])
  public static customerHasSecretNumber(existingVoiceProducts: VoiceProduct[]): boolean {
    return existingVoiceProducts.filter(p => p.subscription.isSecret).length > 0;
  }

  @Selector([CustomerProductState])
  public static selectedStep(state: CustomerProductsStateModel): number {
    return state.selectedStepIndex;
  }

  @Selector([CustomerProductState])
  public static selectedSubscriptionId(state: CustomerProductsStateModel): string {
    return state.selectedSubscriptionId;
  }

  @Selector([CustomerProductState])
  public static selectedSubscription(state: CustomerProductsStateModel): VoiceProduct {
    return state.products?.find(product => product.id === state.selectedSubscriptionId);
  }

  @Selector([CustomerProductState])
  public static numberOfTerminalBindingsWithoutMobileProduct(state: CustomerProductsStateModel): number {
    const newVoiceProducts = state.products.filter(p => !p.isExistingSubscription);

    // check if there is a new offering with terminal binding without a mobile connected to it and without an accessory
    if (newVoiceProducts.length > 0) {
      const newProductsMedTerminalBinding = newVoiceProducts.filter(
        product =>
          product.offering.bindings?.length > 0 &&
          product.offering.bindings.find(b => b.bindingType === BindingType.Handset) &&
          !product.mobileProductId &&
          !product.accessoryProductId
      );
      return newProductsMedTerminalBinding.length;
    }

    return 0;
  }

  @Selector([CustomerProductState])
  public static allowedPortingDates(state: CustomerProductsStateModel): PortingWindowsResponse {
    return state.portingDates;
  }

  @Selector([CustomerProductState])
  public static isLoadingPortingDates(state: CustomerProductsStateModel): boolean {
    return state.isLoadingPortingDates;
  }

  @Selector([CustomerProductState])
  public static isLoadingExistingProductDetails(state: CustomerProductsStateModel): boolean {
    return state.isLoadingExistingProductDetails;
  }

  @Selector([CustomerProductSelectors.getExistingVoiceProducts, CustomerSelectors.getState])
  static getExistingVoiceSubscriptionsAvailableForEquipmentUpgrade(
    existingVoiceProducts: VoiceProduct[],
    customer: CustomerStateModel
  ): VoiceProduct[] {
    if (customer.isExistingCustomer && customer.hasPerformedAllowanceCheck && customer.authorizeResult.equipmentUpgrade) {
      if (
        customer.authorizeResult.equipmentUpgrade.noOfSubsAvailableForEquipmentUpgrade > 0 &&
        customer.authorizeResult.equipmentUpgrade.existingCompatibleSubscriptions
      ) {
        const compatibleMsisdns = customer.authorizeResult.equipmentUpgrade.existingCompatibleSubscriptions
          .filter(s => !s.quarantineEndDate)
          .map(s => s.msisdn);
        return existingVoiceProducts.filter(voiceProduct => compatibleMsisdns.includes(voiceProduct.subscription.msisdn));
      }
    }
    return [];
  }

  @Selector([CustomerProductSelectors.getExistingVoiceProducts, CustomerSelectors.getState])
  static getExistingVoiceSubscriptionsAvailableForEquipmentUpgradeUpgrade(
    existingVoiceProducts: VoiceProduct[],
    customer: CustomerStateModel
  ): VoiceProduct[] {
    if (customer.isExistingCustomer && customer.hasPerformedAllowanceCheck && customer.authorizeResult.equipmentUpgrade) {
      if (
        customer.authorizeResult.equipmentUpgrade.hasSubAvailableForRedemption &&
        customer.authorizeResult.equipmentUpgrade.existingCompatibleSubscriptions
      ) {
        const compatibleMsisdns = customer.authorizeResult.equipmentUpgrade.existingCompatibleSubscriptions
          .filter(s => !s.quarantineEndDate)
          .map(s => s.msisdn);
        return existingVoiceProducts.filter(voiceProduct => compatibleMsisdns.includes(voiceProduct.subscription.msisdn));
      }
    }
    return [];
  }

  @Selector([
    CustomerSelectors.isCustomerAllowedToAddAdditionalSubscriptions,
    CustomerSelectors.numberOfSubscriptionAllowed,
    CustomerProductSelectors.getAllVoiceProducts,
  ])
  static isSubscriptionLimitReached(
    isCustomerAllowedToAddAdditionalSubscriptions: boolean,
    numberOfSubscriptionAllowed: number,
    allVoiceProducts: VoiceProduct[]
  ): boolean {
    const isCurrentSubscriptionAmountReached = allVoiceProducts.length >= numberOfSubscriptionAllowed;
    return (
      numberOfSubscriptionAllowed > 0 &&
      (isCurrentSubscriptionAmountReached || isCustomerAllowedToAddAdditionalSubscriptions)
    );
  }

  @Selector([CustomerProductSelectors.getAllVoiceProducts, CustomerSelectors.numberOfSubscriptionAllowed])
  static isSubscriptionLimitExceeded(allVoiceProducts: VoiceProduct[], numberOfSubscriptionAllowed: number): boolean {
    return allVoiceProducts.length > numberOfSubscriptionAllowed;
  }

  @Selector([CustomerProductSelectors.getAllVoiceProducts])
  public static voiceProductsWithShipping(allVoiceProducts: VoiceProduct[]): VoiceProduct[] {
    return allVoiceProducts.filter(
      voiceProduct =>
        (voiceProduct.phoneNumber && voiceProduct.phoneNumber.orderSim) ||
        voiceProduct.additionalServices.some(additionalService => additionalService.orderSim)
    );
  }

  @Selector([CustomerProductSelectors.getAllVoiceProducts])
  static bundledVoiceProducts(allVoiceProducts: VoiceProduct[]): VoiceProduct[] {
    return allVoiceProducts.filter(voiceProduct => Boolean(voiceProduct.mobileProductId || voiceProduct.accessoryProductId));
  }

  @Selector([CustomerProductState])
  static getVoiceProductByMsisdn(state: CustomerProductsStateModel): (msisdn: string) => VoiceProduct | undefined {
    return (msisdn: string): VoiceProduct | undefined => {
      return state.products.find(product => product.subscription.msisdn === msisdn);
    };
  }

  @Selector([CustomerProductState])
  static isLoadingAssignedSubscriptions(state: CustomerProductsStateModel): boolean {
    return state.isLoadingAssignedSubscriptions;
  }

  @Selector([CustomerProductSelectors.getAllVoiceProducts])
  static hasDuplicateMsisdns(voiceProducts: VoiceProduct[]): boolean {
    const uniqueMsisdns = new Set<string>();

    for (const product of voiceProducts) {
      const msisdn = product.phoneNumber?.aNumber;
      if (!msisdn) continue;

      if (uniqueMsisdns.has(msisdn)) {
        return true;
      }

      uniqueMsisdns.add(msisdn);
    }

    return false;
  }

  @Selector([CustomerProductSelectors.getAllVoiceProducts])
  static voiceProductIdsWithDuplicateMsisdns(voiceProducts: VoiceProduct[]): string[] {
    const uniqueMsisdns = new Set<string>();
    const duplicateMsisdns = [];

    for (const product of voiceProducts) {
      const msisdn = product.phoneNumber?.aNumber;
      if (!msisdn) continue;

      if (uniqueMsisdns.has(msisdn)) {
        duplicateMsisdns.push(msisdn);
      }

      uniqueMsisdns.add(msisdn);
    }

    if (duplicateMsisdns.length < 1) return [];

    return (
      voiceProducts
        .filter(product => duplicateMsisdns.includes(product.phoneNumber?.aNumber || ''))
        .map(product => product.id) || []
    );
  }
}
